import { logger } from '@/core/app/logger'
import {
  type AuthIdentity,
  fetchCookieIdentity,
  identityFromTokenStore,
  shouldReloadForMemberChange,
} from '@/core/app/restoredAuth'
import { isValidCampaignSlug } from '@/core/app/useCampaignSlug'
import { useTokenStore } from '@/store/useTokenStore'
import { useEffect } from 'react'

type MemberCheckDeps = {
  campaignSlug: string
  getTokenState: () => { idToken: string | null; loading: boolean }
  fetchCookieIdentity: (campaignSlug: string) => Promise<AuthIdentity>
  reload: () => void
}

/**
 * Cookie 上の会員がページの会員と違えばページを読み直す（#3760）。テストできるよう依存を引数で受け取る。
 * 判定が終わるまで待つ Promise を返す（何もしないときは即時解決）。
 *
 * @param trigger ログに出すきっかけ（'bfcache 復元' / 'タブ復帰'）
 */
export const checkMemberAndReload = async (deps: MemberCheckDeps, trigger: string): Promise<void> => {
  if (!isValidCampaignSlug(deps.campaignSlug)) return
  // 問い合わせ前に読む。問い合わせ中に phase3 の tokenRefresh がストアを書き換えても、比べるのは確認を始めた時点の会員
  const inMemory = identityFromTokenStore(deps.getTokenState())
  if (inMemory.kind === 'unknown') return
  const inCookie = await deps.fetchCookieIdentity(deps.campaignSlug)
  if (!shouldReloadForMemberChange(inMemory, inCookie)) return
  logger.info({ xstate: 'lifecycle', msg: `${trigger}: 会員が変わっていたためページを読み直す` })
  deps.reload()
}

/**
 * checkMemberAndReload を、確認中は重ねて走らせない形で包む（#3760）。
 * bfcache から復元されると pageshow と visibilitychange が続けて発火するため、1 回の問い合わせにまとめる。
 */
export const createMemberChangeChecker = (deps: MemberCheckDeps): ((trigger: string) => Promise<void>) => {
  let checking = false
  return async (trigger) => {
    if (checking) return
    checking = true
    try {
      await checkMemberAndReload(deps, trigger)
    } finally {
      checking = false
    }
  }
}

/**
 * ページが再び表示されたとき、Cookie 上の会員がページの会員と違えばページを読み直す（#3760）。
 * _app（AppContent）で 1 回だけ呼ぶ。
 *
 * - bfcache から復元されたとき（pageshow の persisted）: JS のメモリ（useTokenStore の IdToken）を持ったまま再開し、
 *   checkAuth が走らない
 * - 裏に回っていたタブが表に戻ったとき（visibilitychange）: ページを離れていないので bfcache も通らない。
 *   PAGE_VISIBLE はトークンが期限切れ間近のときしかリフレッシュしない
 *
 * どちらも、別タブで別アカウントに入り直していると、前の会員のトークンで API を呼び続けてしまう。
 * AR カメラ表示中の読み直し抑止（pageReloadSuppressed）は見ない。会員の取り違えは正しさの問題なので優先する。
 */
export const useReloadOnMemberChange = (campaignSlug: string): void => {
  useEffect(() => {
    const check = createMemberChangeChecker({
      campaignSlug,
      getTokenState: () => useTokenStore.getState(),
      fetchCookieIdentity,
      reload: () => window.location.reload(),
    })
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) void check('bfcache 復元')
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void check('タブ復帰')
    }
    window.addEventListener('pageshow', handlePageShow)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.removeEventListener('pageshow', handlePageShow)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [campaignSlug])
}
